/**
 * AccountFilters
 *
 * FilterBar wrapper with tabs, search, sort, and field filters for the
 * Chart of Accounts page.
 */

import { Card, FilterBar } from '@elsapiens/ui'

import { buildTabDefinitions } from './chart-of-accounts-helpers'
import { ACCOUNT_TYPE_LABELS } from '../../constants/labels'

import type { FilterBarCondition } from '@elsapiens/ui'


interface AccountFiltersProps {
  searchQuery: string
  onSearchChange: (q: string) => void
  filterConditions: FilterBarCondition[]
  onFilterConditionsChange: (conds: FilterBarCondition[]) => void
  activeTab: string
  onTabChange: (tabId: string) => void
  sortField: string
  sortDirection: 'asc' | 'desc'
  onSortChange: (id: string, dir: 'asc' | 'desc') => void
  subtypeOptions: Array<{ value: string; label: string }>
  currencyOptions: Array<{ value: string; label: string }>
}

const ACCOUNT_TYPE_OPTIONS = Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => ({ value, label }))

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

const KIND_OPTIONS = [
  { value: 'header', label: 'Header (Group)' },
  { value: 'leaf', label: 'Leaf (Posting)' },
]

const SORT_OPTIONS = [
  { id: 'code', label: 'Account Code' },
  { id: 'name', label: 'Account Name' },
]

const TAB_DEFS = buildTabDefinitions()

const FILTER_BAR_TABS = [
  { id: 'active', label: 'Active', conditions: TAB_DEFS[0]?.conditions ?? [] },
  { id: 'all', label: 'All', conditions: [] },
  { id: 'headers', label: 'Headers Only', conditions: TAB_DEFS[2]?.conditions ?? [] },
]

function buildFilterFields(
  subtypeOptions: AccountFiltersProps['subtypeOptions'],
  currencyOptions: AccountFiltersProps['currencyOptions'],
): React.ComponentProps<typeof FilterBar>['fields'] {
  return [
    { id: 'accountType', label: 'Account Type', type: 'select', options: ACCOUNT_TYPE_OPTIONS },
    { id: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
    { id: 'subtype', label: 'Subtype', type: 'select', options: subtypeOptions },
    { id: 'currency', label: 'Currency', type: 'select', options: currencyOptions },
    { id: 'kind', label: 'Kind', type: 'select', options: KIND_OPTIONS },
  ]
}

export function AccountFilters({
  searchQuery,
  onSearchChange,
  filterConditions,
  onFilterConditionsChange,
  activeTab,
  onTabChange,
  sortField,
  sortDirection,
  onSortChange,
  subtypeOptions,
  currencyOptions,
}: AccountFiltersProps): JSX.Element {
  // Split tab-managed conditions from user-added conditions.
  // Tab conditions must not be passed to FilterBar — they would auto-open the
  // Custom panel and appear as removable pills.
  const tabConditions = filterConditions.filter(c => c.id.startsWith('tab-'))
  const userConditions = filterConditions.filter(c => !c.id.startsWith('tab-'))

  function handleTabChange(tabId: string): void {
    onTabChange(tabId)
    const tab = TAB_DEFS.find(t => t.id === tabId)
    onFilterConditionsChange([...userConditions, ...(tab?.conditions ?? [])])
  }

  function handleUserConditionsChange(newUserConds: FilterBarCondition[]): void {
    onFilterConditionsChange([...tabConditions, ...newUserConds])
  }

  return (
    <Card className="el-mb-lg overflow-hidden">
      <FilterBar
        firstInCard
        inCard
        lastInCard
        activeTab={activeTab}
        conditions={userConditions}
        fields={buildFilterFields(subtypeOptions, currencyOptions)}
        search={{
          value: searchQuery,
          onChange: onSearchChange,
          placeholder: 'Search by code, name, subtype, or tag...',
        }}
        sort={{
          options: SORT_OPTIONS,
          value: sortField,
          direction: sortDirection,
          onChange: onSortChange,
        }}
        tabs={FILTER_BAR_TABS}
        onConditionsChange={handleUserConditionsChange}
        onTabChange={handleTabChange}
      />
    </Card>
  )
}
